/**
 * The icons the toolbar draws, as components rather than as inline SVG.
 *
 * These were written inline inside the buttons on the reading screen, which was
 * fine while the buttons were the only place they appeared. Help (DYS-050) names
 * these controls - "Dostosuj", "Odtwórz", "Eksportuj" - and has to show the same
 * glyph the reader is being told to look for, so a second copy pasted into the
 * help content would be a second copy to keep in step. There is one now.
 *
 * None of them declares a size or a colour. The strokes were already
 * `currentColor` and the hard-coded 20px the buttons carried is a className now,
 * so each site sets both from its own context. The 24 viewBox and the 2px stroke
 * are lucide's, which is what lets these sit beside lucide icons at the same
 * nominal size without looking lighter than they do.
 *
 * All three are `aria-hidden`: every one of them appears next to its own label,
 * in the toolbar as an `aria-label` on the button and in Help as the name of the
 * control in the sentence beside it. Announcing them again would read the same
 * word twice.
 */

interface AppIconProps {
  className?: string
}

/** Text formatting: the button that opens the "Dostosuj" drawer, and its heading. */
export const TextFormatIcon = ({ className }: AppIconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path d="M15 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15 6H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3 13L6.553 5.27597C6.59457 5.19303 6.6584 5.12329 6.73734 5.07455C6.81628 5.02581 6.90723 5 7 5C7.09277 5 7.18372 5.02581 7.26266 5.07455C7.3416 5.12329 7.40543 5.19303 7.447 5.27597L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3.91992 11H10.0799" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

/** Read aloud. The pause glyph is lucide's `Pause`, used only while speaking. */
export const SpeakerIcon = ({ className }: AppIconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path d="M11 4.70203C10.9998 4.56274 10.9583 4.42663 10.8809 4.31088C10.8034 4.19514 10.6934 4.10493 10.5647 4.05166C10.436 3.99838 10.2944 3.98442 10.1577 4.01154C10.0211 4.03866 9.89559 4.10564 9.797 4.20403L6.413 7.58703C6.2824 7.7184 6.12703 7.82256 5.95589 7.89345C5.78475 7.96435 5.60124 8.00057 5.416 8.00003H3C2.73478 8.00003 2.48043 8.10539 2.29289 8.29292C2.10536 8.48046 2 8.73481 2 9.00003V15C2 15.2652 2.10536 15.5196 2.29289 15.7071C2.48043 15.8947 2.73478 16 3 16H5.416C5.60124 15.9995 5.78475 16.0357 5.95589 16.1066C6.12703 16.1775 6.2824 16.2817 6.413 16.413L9.796 19.797C9.8946 19.8958 10.0203 19.9631 10.1572 19.9904C10.2941 20.0177 10.436 20.0037 10.5649 19.9503C10.6939 19.8968 10.804 19.8063 10.8815 19.6902C10.959 19.5741 11.0002 19.4376 11 19.298V4.70203Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M16 9C16.6491 9.86548 17 10.9181 17 12C17 13.0819 16.6491 14.1345 16 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

/** Save the recognised text to the device - labelled "Eksportuj" in the toolbar. */
export const ExportIcon = ({ className }: AppIconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)
